import Axios from 'axios';

/**
 * Fetch /api/images/:image_id/data as an arraybuffer and return a base64
 * dataURL string ready to feed an <img src=…> or fabric.Image.
 *
 * Uses the browser-portable Uint8Array → btoa path so it does not depend on
 * Node's `Buffer` polyfill (some bundler configs ship without it). The chunked
 * loop keeps the intermediate binary string short enough to avoid the call-stack
 * blow-up `String.fromCharCode(...bytes)` causes on large payloads.
 *
 * @param {string} imageId               Mongo OID of the image.
 * @param {object} [opts]
 * @param {string} [opts.baseUrl]        Override the API base (defaults to REACT_APP_SERVER_URL).
 * @param {AbortSignal} [opts.signal]    Pass through to Axios for cancellation.
 * @returns {Promise<string>}            `data:<content-type>;base64,<...>`.
 */
export async function fetchImageDataUrl(imageId, opts = {}) {
	const baseUrl = opts.baseUrl ?? process.env.REACT_APP_SERVER_URL;
	const res = await Axios.get(`${baseUrl}/api/images/${imageId}/data`, {
		responseType: 'arraybuffer',
		signal: opts.signal,
	});

	const contentType = res.headers?.['content-type'] || 'image/jpeg';
	return arrayBufferToDataUrl(res.data, contentType);
}

/**
 * Browser-safe arrayBuffer → base64 dataURL conversion.
 * Chunks the binary string to keep `String.fromCharCode.apply` happy for
 * multi-MB payloads.
 */
export function arrayBufferToDataUrl(buffer, contentType) {
	const bytes = new Uint8Array(buffer);
	const CHUNK = 0x8000;
	let binary = '';
	for (let i = 0; i < bytes.length; i += CHUNK) {
		const slice = bytes.subarray(i, i + CHUNK);
		binary += String.fromCharCode.apply(null, slice);
	}
	return `data:${contentType};base64,${btoa(binary)}`;
}

export default fetchImageDataUrl;
